<script lang="ts">
  import { _, locale } from 'svelte-i18n';
  import { createEventDispatcher } from 'svelte';
  import { Link } from 'svelte-routing';
  import type {
    ReportEntryDto,
    ReportFilterField,
    HouseholdMember,
    DataSourceInfo,
  } from '../../api';
  import { userSettings } from '../../stores/settingsStore';
  import { formatDate } from '../../settings';
  import { getLocalizedClassificationCategoryName } from '../../categoriesLocalization';
  import { FileText, Info } from 'lucide-svelte';
  import {
    Spinner,
    TableSortHeader,
    FilterToolbar,
    PaginationBar,
    AiDisclaimer,
  } from '../ui/index';
  import type { FilterDef, FilterValue } from '../ui/FilterToolbar.svelte';

  export let entries: ReportEntryDto[] = [];
  export let loading = false;
  export let sortBy: string | null = 'entry_date';
  export let sortOrder: 'asc' | 'desc' | null = 'desc';
  export let filterState: Record<string, string | number | null> = {};
  export let page = 0;
  export let pageSize = 20;
  export let totalCount: number | null = null;
  export let hasMore = false;
  export let filterSchema: ReportFilterField[] = [];
  export let members: HouseholdMember[] = [];
  export let dataSources: DataSourceInfo[] = [];

  $: memberMap = Object.fromEntries(members.map((m) => [m.id, m.name]));

  const dispatch = createEventDispatcher<{
    sort: { key: string; order: 'asc' | 'desc' | null };
    filter: { key: string; value: FilterValue };
    clearAll: void;
    pageChange: number;
    pageSizeChange: number;
  }>();

  function formatAmount(amount: unknown, settings: { locale: string }): string {
    const num = Number(amount);
    if (isNaN(num)) return '-';
    return num.toLocaleString(settings.locale, { style: 'currency', currency: 'EUR' });
  }

  function handleSort(e: CustomEvent<{ key: string; order: 'asc' | 'desc' | null }>) {
    dispatch('sort', e.detail);
  }

  // Year options for date filter
  const yearOptions = (() => {
    const currentYear = new Date().getFullYear();
    const years: string[] = [];
    for (let y = currentYear; y >= currentYear - 10; y--) {
      years.push(y.toString());
    }
    return years;
  })();

  // Build filters dynamically from backend schema, with fallback to hardcoded defaults
  $: availableFilters =
    filterSchema.length > 0
      ? buildFiltersFromSchema(filterSchema, yearOptions, $_, members)
      : ([
          {
            key: 'year',
            label: $_('reports.table.date'),
            type: 'enum' as const,
            options: yearOptions,
            optionLabels: yearOptions,
          },
          { key: 'category', label: $_('reports.table.category'), type: 'text' as const },
          { key: 'amount', label: $_('reports.table.amount'), type: 'number-range' as const },
        ] satisfies FilterDef[]);

  /**
   * Translates a filter option value via i18n.
   * Looks up: reports.filter_options.<fieldName>.<optionValue> (dots replaced with underscores).
   * Falls back to the raw option value if no translation is found.
   */
  function translateFilterOption(
    fieldName: string,
    option: string,
    t: (key: string) => string,
  ): string {
    const key = `reports.filter_options.${fieldName}.${option.replace(/-/g, '_')}`;
    const translated = t(key);
    // svelte-i18n returns the key itself if no translation is found
    return translated !== key ? translated : option;
  }

  function buildFiltersFromSchema(
    schema: ReportFilterField[],
    years: string[],
    t: (key: string) => string,
    currentMembers: HouseholdMember[],
  ): FilterDef[] {
    const filters: FilterDef[] = [];
    // Track which fields have been consumed as part of a range pair
    const rangeConsumed = new Set<string>();

    for (const field of schema) {
      if (rangeConsumed.has(field.name)) continue;

      if (field.name === 'year') {
        // Year is always an enum with generated year options
        filters.push({
          key: 'year',
          label: field.displayName,
          type: 'enum',
          options: years,
          optionLabels: years,
        });
      } else if (field.name === 'person' && currentMembers.length > 0) {
        // Person filter: use household members as enum options
        filters.push({
          key: 'person',
          label: field.displayName,
          type: 'enum',
          options: currentMembers.map((m) => m.name),
          optionLabels: currentMembers.map((m) => m.name),
        });
      } else if (field.fieldType === 'Select' && field.options && field.options.length > 0) {
        filters.push({
          key: field.name,
          label: field.displayName,
          type: 'enum',
          options: field.options,
          optionLabels: field.options.map((o) => translateFilterOption(field.name, o, t)),
        });
      } else if (field.fieldType === 'Decimal') {
        // Check if there's a matching min/max pair for a range filter
        const pairName =
          field.name === 'minAmount'
            ? 'maxAmount'
            : field.name === 'maxAmount'
              ? 'minAmount'
              : null;
        if (pairName && schema.some((f) => f.name === pairName)) {
          rangeConsumed.add(field.name);
          rangeConsumed.add(pairName);
          filters.push({ key: 'amount', label: t('reports.table.amount'), type: 'number-range' });
        } else {
          filters.push({ key: field.name, label: field.displayName, type: 'text' });
        }
      } else if (field.fieldType === 'String') {
        filters.push({ key: field.name, label: field.displayName, type: 'text' });
      }
    }
    return filters;
  }

  // Map filterState to toolbar-compatible activeFilters
  $: toolbarFilters = buildToolbarFilters(filterState);

  function buildToolbarFilters(
    state: Record<string, string | number | null>,
  ): Record<string, FilterValue> {
    const result: Record<string, FilterValue> = {};
    if (state.year) result.year = [String(state.year)];
    if (state.category) {
      // Wrap as array if filter is enum type (Select from schema)
      const categoryFilter = availableFilters.find((f) => f.key === 'category');
      result.category =
        categoryFilter?.type === 'enum' ? [String(state.category)] : String(state.category);
    }
    if (state.person) result.person = [String(state.person)];
    if (state.minAmount != null || state.maxAmount != null)
      result.amount = {
        min: state.minAmount != null ? Number(state.minAmount) : null,
        max: state.maxAmount != null ? Number(state.maxAmount) : null,
      };
    return result;
  }

  function handleFilterChange(e: CustomEvent<{ key: string; value: FilterValue }>) {
    dispatch('filter', e.detail);
  }

  function handleFilterRemove(e: CustomEvent<{ key: string }>) {
    dispatch('filter', { key: e.detail.key, value: null });
  }

  function handleClearAll() {
    dispatch('clearAll');
  }

  // Aggregate total amount for entries with a parseable amount value (Spec 012 E5, #7).
  // Shown as a tfoot row so the user can see the page total at a glance.
  $: pageTotal = entries.reduce((sum, e) => {
    const n = Number(e.data?.amount);
    return isNaN(n) ? sum : sum + n;
  }, 0);
  $: hasAmountEntries = entries.some((e) => e.data?.amount != null);
</script>

<div class="bg-surface border border-border rounded-lg overflow-hidden">
  <div data-testid="report-year-filter">
    <FilterToolbar
      {availableFilters}
      activeFilters={toolbarFilters}
      on:filterChange={handleFilterChange}
      on:filterRemove={handleFilterRemove}
      on:clearAll={handleClearAll}
    />
  </div>

  {#if loading}
    <div class="flex justify-center py-12">
      <Spinner size="lg" label={$_('messages.loading')} />
    </div>
  {:else if entries.length === 0}
    <div class="flex flex-col items-center justify-center py-12 px-4 text-center">
      <FileText class="w-8 h-8 text-text-muted mb-2" strokeWidth={1.5} />
      <p class="text-sm text-text-secondary">{$_('reports.no_entries')}</p>
    </div>
  {:else}
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="border-b border-border">
            <th
              class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-muted"
            >
              {$_('reports.table.tax_year')}
            </th>
            <TableSortHeader
              label={$_('reports.table.date')}
              sortKey="entry_date"
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              on:sort={handleSort}
            />
            <TableSortHeader
              label={$_('reports.table.tax_category')}
              sortKey="tax_category"
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              thClass="hidden sm:table-cell"
              on:sort={handleSort}
            />
            <th
              class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-muted hidden md:table-cell"
            >
              {$_('reports.table.persons')}
            </th>
            <TableSortHeader
              label={$_('reports.table.amount')}
              sortKey="amount"
              currentSortBy={sortBy}
              currentSortOrder={sortOrder}
              on:sort={handleSort}
            />
            <th
              class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-muted hidden sm:table-cell"
            >
              {$_('reports.table.document')}
            </th>
          </tr>
        </thead>
        <tbody>
          {#each entries as entry (entry.id)}
            <tr
              data-testid="report-row-{entry.documentId}"
              class="border-b border-border/50 transition-colors hover:bg-surface-hover"
            >
              <td class="px-4 py-3 text-sm font-medium text-text whitespace-nowrap"
                >{entry.year ?? '-'}</td
              >
              <td class="px-4 py-3 text-sm text-text-muted whitespace-nowrap"
                >{formatDate(entry.entryDate, $userSettings)}</td
              >
              <td class="px-4 py-3 text-sm text-text hidden sm:table-cell"
                >{entry.taxCategory ||
                  getLocalizedClassificationCategoryName(entry.category, $locale)}</td
              >
              <td class="px-4 py-3 text-sm text-text-secondary hidden md:table-cell">
                {#if entry.persons.length > 0}
                  {entry.persons.map((id) => memberMap[id] ?? id).join(', ')}
                {:else}
                  <span class="text-text-muted">-</span>
                {/if}
              </td>
              <td class="px-4 py-3 text-sm font-mono text-right whitespace-nowrap text-text">
                {#if entry.data?.amount != null}
                  {formatAmount(entry.data.amount, $userSettings)}
                {:else}
                  <span class="text-text-muted">-</span>
                {/if}
              </td>
              <td class="px-4 py-3 hidden sm:table-cell">
                <Link
                  to="/documents/{entry.documentId}"
                  class="text-sm text-accent hover:text-accent-hover hover:underline"
                >
                  {entry.data?.displayName || entry.data?.fileName || entry.documentId}
                </Link>
              </td>
            </tr>
          {/each}
        </tbody>
        {#if hasAmountEntries}
          <tfoot>
            <tr class="border-t-2 border-border bg-surface-hover/30">
              <td colspan="4" class="px-4 py-3 text-sm font-semibold text-text text-right">
                {$_('reports.table.total_amount')}
              </td>
              <td
                class="px-4 py-3 text-sm font-mono font-semibold text-right whitespace-nowrap text-text"
                data-testid="report-total-amount"
              >
                {formatAmount(pageTotal, $userSettings)}
              </td>
              <td class="hidden sm:table-cell"></td>
            </tr>
          </tfoot>
        {/if}
      </table>
    </div>

    <PaginationBar
      {page}
      {pageSize}
      {totalCount}
      {hasMore}
      hasPrev={page > 0}
      on:pageChange={(e) => dispatch('pageChange', e.detail)}
      on:pageSizeChange={(e) => dispatch('pageSizeChange', e.detail)}
    />

    <AiDisclaimer>
      {#if dataSources.length > 0}
        <div class="flex items-start gap-2">
          <Info class="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-info" />
          <div>
            <p class="font-semibold text-text-secondary mb-1">{$_('reports.data_sources_title')}</p>
            <p class="mb-1">{$_('reports.data_sources_description')}</p>
            <ul class="list-disc list-inside space-y-0.5">
              {#each dataSources as source (source.label)}
                <li>{source.label}</li>
              {/each}
            </ul>
          </div>
        </div>
      {/if}
    </AiDisclaimer>
  {/if}
</div>
